import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useEffect, useState } from "react";

import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Chip,
  Avatar,
  Spinner,
  Divider,
} from "@heroui/react";
import {
  Eye,
  Calendar,
  Clock,
  UserCheck,
  Shield,
  User,
  Search,
  Divide,
} from "lucide-react";
import { getMembers } from "@/api/user";
import type { Member } from "@/api/user.type";
import dayjs from "dayjs";
import { useHttpRequest } from "@/hooks/useHttpRequest";

const searchSchema = z.object({
  page: z.number().min(1).catch(1),
  size: z.number().min(10).max(100).catch(20),
  search: z.string().optional(),
});

export const Route = createFileRoute("/(authenticated)/users/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { page, size, search } = Route.useSearch();
  const [searchInput, setSearchInput] = useState(search || "");

  const {
    data: membersResponse,
    error,
    isLoading,
    request: requestMembers,
  } = useHttpRequest(() =>
    getMembers({ page, size, email: search }).then((res) => res)
  );

  const totalCount = membersResponse?.totalCount || 0;
  const totalPages = membersResponse?.totalPage || 0;
  const members = membersResponse?.data || [];

  const handlePageChange = (newPage: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const getRoleChipColor = (role: Member["role"]) => {
    switch (role) {
      case "ADMIN":
        return "danger";
      case "MANAGER":
        return "primary";
      case "MEMBER":
        return "default";
      default:
        return "default";
    }
  };

  const getRoleIcon = (role: Member["role"]) => {
    switch (role) {
      case "ADMIN":
        return <Shield className="h-3 w-3" />;
      case "MANAGER":
        return <UserCheck className="h-3 w-3" />;
      case "MEMBER":
        return <User className="h-3 w-3" />;
      default:
        return <User className="h-3 w-3" />;
    }
  };

  const handleViewUser = (userId: number) => {
    navigate({
      to: "/users/$id",
      params: { id: userId.toString() },
    });
  };

  const handleSearch = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        search: searchInput || undefined,
      }),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        search: undefined,
      }),
    });
  };

  useEffect(() => {
    requestMembers();
  }, [page, size, search]);

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardBody className="pt-6">
            <div className="text-center text-danger">
              멤버 목록을 불러오는 중 오류가 발생했습니다.
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2 space-y-6">
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <h1 className="text-2xl font-bold">멤버 목록</h1>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner size="lg" color="primary" />
        </div>
      ) : (
        <>
          <Card shadow="sm">
            <CardHeader className="pb-3">
              <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 w-full">
                <div className="flex items-center space-x-2">
                  <Chip size="lg" variant="flat">
                    총 {totalCount}명
                  </Chip>
                </div>
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
                  <Input
                    placeholder="이메일로 검색..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    startContent={
                      <Search className="h-4 w-4 text-default-400" />
                    }
                    isClearable
                    onClear={() => setSearchInput("")}
                    className="w-full sm:w-64"
                    variant="bordered"
                  />
                  <div className="flex space-x-2">
                    <Button
                      color="primary"
                      onPress={handleSearch}
                      size="md"
                      className="flex-1 sm:flex-none"
                    >
                      검색
                    </Button>
                    {search && (
                      <Button
                        color="default"
                        variant="bordered"
                        onPress={handleClearSearch}
                        size="md"
                        className="flex-1 sm:flex-none"
                      >
                        초기화
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <Divider />
            <CardBody className="p-0">
              <Table
                aria-label="멤버 목록 테이블"
                classNames={{
                  td: "border-b border-divider/50",
                }}
                shadow="none"
              >
                <TableHeader>
                  <TableColumn>이름</TableColumn>
                  <TableColumn className="hidden sm:table-cell">
                    이메일
                  </TableColumn>
                  <TableColumn>역할</TableColumn>
                  <TableColumn className="hidden md:table-cell">
                    가입일
                  </TableColumn>

                  <TableColumn align="end">액션</TableColumn>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.uid}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="min-w-0">
                            <div className="font-medium">{member.name}</div>
                            <div className="text-sm text-default-500 sm:hidden truncate">
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {member.email}
                      </TableCell>
                      <TableCell>
                        <Chip
                          variant="dot"
                          startContent={getRoleIcon(member.role)}
                          size="sm"
                        >
                          {member.role}
                        </Chip>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center space-x-1 text-sm">
                          <span>
                            {dayjs(member.createdAt).format("YYYY.MM.DD")}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Button
                          color="secondary"
                          variant="solid"
                          size="sm"
                          onPress={() => handleViewUser(member.uid)}
                          startContent={<Eye className="h-3 w-3" />}
                        >
                          <span className="hidden sm:inline">상세보기</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardBody>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                total={totalPages}
                page={page}
                onChange={handlePageChange}
                showControls
                showShadow
                color="primary"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
